import { combineReducers } from 'redux';
import sessionStorage from 'redux-persist/es/storage/session';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { getCurrentRoute } from 'helpers/routes/routes';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { GetUserInfoSuccessType } from 'models/AuthTypes';
import { UserInfo } from 'models/UserInfo';
import { performApiCall } from 'saga/apiClient/ducks';

export declare type AuthUserType = 'internal' | 'external' | 'anonymous';

/* STATE */
export interface AuthState {
  userInfo: UserInfo | null;
}

/* ACTION TYPES */
export enum AuthActionTypes {
  Login = '@@Auth/LOGIN',
  Logout = '@@Auth/LOGOUT',
  GetUserInfo = '@@Auth/GET_USER_INFO',
  RefreshJWT = '@@Auth/REFRESH_JWT',
}

/* ACTIONS */
export const loginActions = createApiActionCreators(AuthActionTypes.Login);
export const logoutActions = createApiActionCreators(AuthActionTypes.Logout);
export const getUserInfoActions = createApiActionCreators(AuthActionTypes.GetUserInfo);
export const refreshJWTActions = createApiActionCreators(AuthActionTypes.RefreshJWT);

/* REDUCERS */
const initialState: AuthState = {
  userInfo: null,
};

const userInfo = createReducer(initialState.userInfo, {
  [AuthActionTypes.GetUserInfo]: {
    [RequestActionTypes.SUCCESS]: (_state: UserInfo | null, payload: GetUserInfoSuccessType) => {
      const userInfoInt = payload.userInfo;

      return {
        ..._state,
        ...userInfoInt,
      };
    },
    [RequestActionTypes.FAILURE]: () => null,
  },
});

export default combineReducers<AuthState>({
  userInfo,
});

/* SELECTORS */
const selectAuth = (state: AppState) => state.auth;

export const selectUserInfo = (state: AppState): UserInfo | null => selectAuth(state).userInfo;

export const selectUserLogged = (state: AppState): boolean => !!selectUserInfo(state)?.logged;

export const selectAuthUserType = (state: AppState): AuthUserType => {
  const userInfo = selectUserInfo(state);

  if (userInfo?.logged) {
    return userInfo.internal ? 'internal' : 'external';
  }

  return 'anonymous';
};

export const selectIsUserInternal = (state: AppState): boolean => selectAuthUserType(state) === 'internal';

/* SAGAS */
function* login() {
  yield put(loginActions.success());
  // ToDo: There should be also 'internalLogin' and 'externalLogin' parameter, but no idea where to get them from. Env probably?
  window.location.href = `${environment.buildProperties.bffAuthUrl}/casLogin?redirectTo=${encodeURIComponent(
    `${window.location.href}`
  )}`;
}

function* logout({ payload: toLogin = true }: any) {
  const isUserInternal: string = yield select(selectIsUserInternal);

  let redirectToUrlParam = 'redirectTo=';
  const reasonLogoutSuccessParam = 'reason=logoutSuccessful';

  if (toLogin) {
    if (isUserInternal) {
      redirectToUrlParam += `${encodeURIComponent(
        `${environment.buildProperties.bffAuthUrl}/casLogin?internalLogin=true&${reasonLogoutSuccessParam}`
      )}`;
    } else {
      redirectToUrlParam += `${encodeURIComponent(
        `${environment.buildProperties.bffAuthUrl}/casLogin?${reasonLogoutSuccessParam}`
      )}`;
    }
  } else {
    redirectToUrlParam += `${environment.home}`;
  }

  window.location.href = `${environment.buildProperties.bffAuthUrl}/casLogout?${redirectToUrlParam}`;
}

async function ssoSessionIdValidation(userInfo: UserInfo) {
  const ssoSessionIdContext: any = await sessionStorage.getItem('ssoSessionId');
  const ssoSessionIdUserInfo: string | undefined = userInfo?.ssoSessionId;
  const ssoSessionIdIsValid: boolean = ssoSessionIdContext && ssoSessionIdContext === ssoSessionIdUserInfo;
  const tmp: any = await sessionStorage.getItem('persist:apfSessionContext');
  const data = tmp !== null && JSON.parse(tmp);

  if (!ssoSessionIdIsValid) {
    // clear sessionStorage context
    const clearSession = async () => {
      if (data.context !== null) {
        delete data.context;
        await sessionStorage.setItem('persist:apfSessionContext', data);
      }
    };

    await clearSession();
  }

  await sessionStorage.setItem('ssoSessionIdContext', `${ssoSessionIdUserInfo}`);
}

async function handleAnonymousJwt(userInfo: UserInfo) {
  if (!userInfo || (!userInfo.logged && !userInfo.anonymous)) {
    if (getCurrentRoute()?.commonPageData.visibleForAnonym) {
      // Get anonymous JWT
      window.location.href = `${environment.buildProperties.bffAuthUrl}/casLogin?redirectTo=${window.location.href}&checkOnly=true`;
    }
  } else if (userInfo.anonymous) {
    await ssoSessionIdValidation(userInfo);
  }
}

function* getUserInfo({ payload }: AppAction<boolean>) {
  const request: ApiClientRequest = {
    apiCall: call(api.getUserInfo, environment.buildProperties.bffAuthUrl),
    skipUserActivityLog: true,
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  yield call(handleGetUserInfo, resp, payload);
}

export function* handleGetUserInfo(resp: ExtendedAxiosResponse, forceUpdate = false) {
  yield call(handleAnonymousJwt, resp.data);

  if (resp.ok) {
    if (!resp.data.logged) {
      yield put(logoutActions.success());
    } else {
      yield call(ssoSessionIdValidation, resp.data);
    }

    const successData: GetUserInfoSuccessType = {
      userInfo: resp.data,
      forceUpdate: forceUpdate,
    };

    yield put(getUserInfoActions.success(successData));
  } else {
    yield put(getUserInfoActions.failure());
  }
}

function* refreshJwt() {
  const request: ApiClientRequest = { apiCall: call(api.refreshJWTToken, environment.buildProperties.bffAuthUrl) };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    window.SHOULD_REFRESH_JWT = false;
  }
}

/* EXPORT */
export function* authSaga() {
  yield takeLatest(createActionType(AuthActionTypes.Login, RequestActionTypes.REQUEST), login);

  yield takeLatest(createActionType(AuthActionTypes.Logout, RequestActionTypes.REQUEST), logout);

  yield takeLatest(createActionType(AuthActionTypes.GetUserInfo, RequestActionTypes.REQUEST), getUserInfo);

  yield takeLatest(createActionType(AuthActionTypes.RefreshJWT, RequestActionTypes.REQUEST), refreshJwt);
}
